import { bcs } from '@mysten/sui/bcs';
import { fromBase64, normalizeSuiAddress } from '@mysten/sui/utils';

import { selectTransferValidationStrategy } from './transfer-strategies/select-transfer-validation-strategy';
import {
  type IAssertInput,
  type ParsedCallArg,
  SendAssertionError,
  UnverifiedCoinOwnershipError,
} from './transfer-strategies/transaction-validation';

export type { IAssertInput } from './transfer-strategies/transaction-validation';
export {
  SendAssertionError,
  UnverifiedCoinOwnershipError,
} from './transfer-strategies/transaction-validation';

const SELF_PAID_GAS_BUDGET_CAP_MIST = 50_000_000n;

// Privy cannot apply the equivalent wallet policy to sponsored gas transactions with ValidDuring expiration.
/**
 * The user signs bytes the server built, so this is the only thing between a
 * compromised or buggy API and an approved transfer. It guards against a bad
 * API, not a bad bundle — stripping this check out defeats it.
 *
 * `expected.amount` is scaled by a `decimals` this module never re-derives, so a
 * wrong scale would stay self-consistent on both sides and pass. Callers verify
 * that scale against the chain's coin metadata before calling.
 */
export function assertSendTransaction({ txBytes, expected }: IAssertInput): void {
  const data = bcs.TransactionData.parse(fromBase64(txBytes));
  if (data.$kind !== 'V1') {
    throw new SendAssertionError('unrecognised transaction format');
  }
  const { V1: v1 } = data;

  if (normalizeSuiAddress(v1.sender) !== normalizeSuiAddress(expected.sender)) {
    throw new SendAssertionError('the sender is not your wallet');
  }

  // Gas payment objects are not part of `inputs`, so nothing below checks them.
  if (normalizeSuiAddress(v1.gasData.owner) === normalizeSuiAddress(expected.sender)) {
    assertSelfPaidGasIsSafe(v1.gasData, expected.ownedGasObjectIds);
  }

  if (v1.kind.$kind !== 'ProgrammableTransaction') {
    throw new SendAssertionError('not a programmable transaction');
  }
  const { inputs, commands } = v1.kind.ProgrammableTransaction;

  assertOwnedInputs(inputs, expected.ownedInputObjectIds);
  const context = {
    inputs,
    commands,
    gasOwner: v1.gasData.owner,
    expiration: v1.expiration,
    expected,
  };
  const strategy = selectTransferValidationStrategy(context);
  strategy(context);
}

function assertSelfPaidGasIsSafe(
  gasData: ReturnType<typeof bcs.GasData.parse>,
  ownedGasObjectIds: string[],
): void {
  const owned = new Set(ownedGasObjectIds.map((id) => normalizeSuiAddress(id)));

  for (const coin of gasData.payment) {
    if (!owned.has(normalizeSuiAddress(coin.objectId))) {
      throw new UnverifiedCoinOwnershipError('it pays gas with an object that is not yours');
    }
  }

  if (BigInt(gasData.budget) > SELF_PAID_GAS_BUDGET_CAP_MIST) {
    throw new SendAssertionError('it sets a gas budget above what a self-paid send allows');
  }
}

function assertOwnedInputs(inputs: ParsedCallArg[], ownedInputObjectIds: string[]): void {
  const owned = new Set(ownedInputObjectIds.map((id) => normalizeSuiAddress(id)));

  for (const input of inputs) {
    if (input.$kind !== 'Object') continue;

    // A shared or receiving object has no single owner to check against.
    if (input.Object.$kind !== 'ImmOrOwnedObject') {
      throw new SendAssertionError(
        'it references an object this control cannot verify ownership of',
      );
    }
    if (!owned.has(normalizeSuiAddress(input.Object.ImmOrOwnedObject.objectId))) {
      throw new UnverifiedCoinOwnershipError('it spends an object that is not yours');
    }
  }
}
